import React, { createContext, useState, useContext, useEffect, useCallback, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSupabase } from '@/components/providers/SystemProvider';

// Define the shape of our exchange rate data
interface ExchangeRate {
  currencyCode: string;
  usdRate: number;
}

// Database record type
interface ExchangeRateRecord {
  id: string;
  currency_code: string;
  usd_rate: number;
  last_updated: string;
  created_at: string;
  updated_at: string;
}

// Define the context shape
interface ExchangeRateContextType {
  rates: Record<string, number>;
  isLoading: boolean;
  error: Error | null;
  refreshRates: () => void;
}

// Create the context with a default undefined value
const ExchangeRateContext = createContext<ExchangeRateContextType | undefined>(undefined);

/**
 * Provider component that fetches and provides exchange rates throughout the app
 */
export const ExchangeRateProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [rates, setRates] = useState<Record<string, number>>({
    USD: 1.0, // USD is always 1.0
  });
  const [error, setError] = useState<Error | null>(null);
  const supabase = useSupabase();

  // Query for all exchange rates
  const { data: ratesData, isLoading: ratesLoading } = useQuery({
    queryKey: ['exchange-rates'],
    queryFn: async () => {
      const { data, error } = await supabase.client
        .from('exchange_rates')
        .select('*');
      
      if (error) throw error;
      return data as ExchangeRateRecord[];
    }
  });
  // Function to seed exchange rates if none exist
  const seedExchangeRates = useCallback(async () => {
    try {
      const defaultRates = [
        { code: 'USD', rate: 1.0 },
        { code: 'GMD', rate: 63.5 },
        { code: 'XOF', rate: 609.75 }
      ];
      
      const now = new Date().toISOString();
      const ratesToInsert = defaultRates.map(rate => ({
        id: globalThis.crypto.randomUUID(),
        currency_code: rate.code,
        usd_rate: rate.rate,
        last_updated: now,
        created_at: now,
        updated_at: now
      }));
      
      const { error } = await supabase.client
        .from('exchange_rates')
        .insert(ratesToInsert);
      
      if (error) throw error;
    } catch (error) {
      setError(new Error('Failed to seed exchange rates'));
    }
  }, [supabase.client]);

  useEffect(() => {
    const processedRates: Record<string, number> = { USD: 1.0 };
    try {
      if (ratesData && ratesData.length > 0) {
        ratesData.forEach(rate => {
          const currencyCode = String(rate.currency_code);
          const usdRate = typeof rate.usd_rate === 'number'
            ? rate.usd_rate
            : parseFloat(String(rate.usd_rate));
          if (currencyCode && !isNaN(usdRate) && usdRate > 0) {
            processedRates[currencyCode] = usdRate;
          }
        });
        setRates(processedRates);
        setError(null);
      } else if (ratesData && ratesData.length === 0) {
        void seedExchangeRates();
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error processing exchange rates'));
    }
  }, [ratesData, ratesLoading, seedExchangeRates]);

  // Function to refresh rates (useful for admin panels)  
  const refreshRates = () => {
    // With React Query, we can just invalidate the query to trigger a refetch
    // This will be implemented when we have access to queryClient
  };

  // Create the context value
  const contextValue: ExchangeRateContextType = {
    rates,
    isLoading: ratesLoading,
    error,
    refreshRates
  };

  return (
    <ExchangeRateContext.Provider value={contextValue}>
      {children}
    </ExchangeRateContext.Provider>
  );
};

/**
 * Hook to use the exchange rate context
 */
export const useExchangeRates = () => {
  const context = useContext(ExchangeRateContext);
  
  if (context === undefined) {
    throw new Error('useExchangeRates must be used within an ExchangeRateProvider');
  }
  
  return context;
};
